/**
 * Shared chart utilities for SVG-based time-series charts.
 * Used by PriceHistoryChart and SalesTimeSeriesChart.
 */

/* ── Theme palette ──────────────────────────────────────── */

/** Default chart color palette (red/dark, matches site aesthetic) */
export const CHART_COLORS = {
  /** Primary line gradient start */
  lineStart: 'var(--color-primary, #DC2626)',
  /** Primary line gradient end */
  lineEnd: 'var(--text-heading, #1A1A1A)',
  /** Area-fill gradient top */
  fillStart: 'rgba(220, 38, 38, 0.20)', // Could be tweaked per theme but rgba is ok
  /** Area-fill gradient bottom (transparent) */
  fillEnd: 'rgba(220, 38, 38, 0.00)',
  /** Dot fill */
  dot: 'var(--color-primary, #DC2626)',
  /** Dot stroke */
  dotStroke: 'var(--surface-card, #ffffff)',
  /** SVG background */
  background: 'transparent',
  /** Grid line */
  gridLine: 'var(--border-subtle, rgba(0, 0, 0, 0.06))',
  /** Axis line */
  axisLine: 'var(--border-default, rgba(0, 0, 0, 0.18))',
  /** X-axis tick */
  axisTick: 'var(--border-strong, rgba(0, 0, 0, 0.22))',
} as const

export type ChartColors = typeof CHART_COLORS

/* ── Layout ─────────────────────────────────────────────── */

export type ChartLayout = {
  width: number
  height: number
  padX: number
  padTop: number
  padBottom: number
}

export const DEFAULT_LAYOUT: ChartLayout = {
  width: 720,
  height: 260,
  padX: 56,
  padTop: 18,
  padBottom: 34,
}

/* ── Scaling helpers ────────────────────────────────────── */

/**
 * Pick a "nice" step size for an axis so tick values are round numbers.
 */
export const niceStep = (rawStep: number): number => {
  if (!Number.isFinite(rawStep) || rawStep <= 0) return 1
  const exponent = Math.floor(Math.log10(rawStep))
  const base = Math.pow(10, exponent)
  const fraction = rawStep / base
  const niceFraction = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10
  return niceFraction * base
}

/**
 * Generate Y-axis tick values within a given min/max range.
 */
export const generateYTicks = (min: number, max: number, count = 5): number[] => {
  const rawStep = (max - min) / (count - 1)
  const step = niceStep(rawStep)
  const startTick = Math.floor(min / step) * step
  const endTick = Math.ceil(max / step) * step
  const ticks: number[] = []
  for (let v = startTick; v <= endTick + step / 2; v += step) {
    if (v >= min - step && v <= max + step) ticks.push(v)
  }
  return ticks
}

/**
 * Build evenly-spaced X-axis tick indexes from a point array.
 * Returns 4 indexes (first, 1/3, 2/3, last) deduplicated.
 */
export const generateXTickIndexes = (length: number): number[] => {
  if (length <= 1) return [0]
  const idxs = [0, Math.floor((length - 1) / 3), Math.floor(((length - 1) * 2) / 3), length - 1]
  return [...new Set(idxs)].sort((a, b) => a - b)
}

/* ── Path helpers ───────────────────────────────────────── */

/**
 * Build an SVG line path (`M … L … L …`) from an array of {x, y} points.
 */
export const buildLinePath = (points: Array<{ x: number; y: number }>): string => {
  if (points.length === 0) return ''
  return points
    .map((p, idx) => (idx === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`))
    .join(' ')
}

/* ── Domain helpers ─────────────────────────────────────── */

/**
 * Compute a data domain (min/max) with an 8 % padding margin.
 * Ensures at least some vertical spread even with flat data.
 */
export const computeDomain = (
  values: number[],
  opts: { clampMin?: number } = {},
): { min: number; max: number } => {
  let min = Math.min(...values)
  let max = Math.max(...values)
  if (min === max) {
    min = Math.max(opts.clampMin ?? -Infinity, min - 1)
    max = max + 1
  }
  const range = Math.max(1, max - min)
  const margin = Math.max(1, Math.round(range * 0.08))
  min = opts.clampMin !== undefined ? Math.max(opts.clampMin, min - margin) : min - margin
  max = max + margin
  return { min, max }
}

/**
 * Create a linear scale function that maps a domain value to pixel position.
 */
export const createScale = (
  domainMin: number,
  domainMax: number,
  rangeStart: number,
  rangeEnd: number,
): ((v: number) => number) => {
  const span = domainMax - domainMin || 1
  return (v: number) => rangeStart + ((v - domainMin) / span) * (rangeEnd - rangeStart)
}

/* ── Date formatting ────────────────────────────────────── */

export const formatAxisDate = (timestamp: number, granularity: 'daily' | 'monthly' = 'daily'): string => {
  const d = new Date(timestamp)
  if (granularity === 'monthly') {
    return d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' })
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
